// Top-level error boundary. Without this, a thrown error during React render
// unmounts the whole tree → blank page with no clue what happened. With this,
// you see a recoverable card with the error message + a "reload" button.
//
// Only catches RENDER errors. Async errors (in event handlers, fetch, etc.)
// need their own try/catch — they don't propagate to error boundaries.

import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] render crash:', error, errorInfo);
    this.setState({ errorInfo });
  }

  reset = (): void => {
    this.setState({ error: null, errorInfo: null });
  };

  reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen bg-bg p-6">
        <div className="max-w-3xl mx-auto bg-paper border-l-4 border-orange rounded shadow p-6">
          <h1 className="text-xl font-bold text-orange mb-3">⚠ Something crashed in the UI</h1>
          <p className="text-sm text-text-mute mb-4">
            React caught a render error and unmounted the page. Details below — share with Claude
            (or check the browser console for the full stack).
          </p>

          <div className="bg-navy text-white rounded p-3 mb-4 text-xs font-mono overflow-x-auto">
            <div className="font-bold mb-1">{this.state.error.name}: {this.state.error.message}</div>
            {this.state.error.stack && (
              <pre className="whitespace-pre-wrap text-xs opacity-80">{this.state.error.stack}</pre>
            )}
          </div>

          {this.state.errorInfo?.componentStack && (
            <details className="mb-4 text-xs">
              <summary className="cursor-pointer text-text-mute hover:text-text">
                Component stack
              </summary>
              <pre className="mt-2 bg-bg p-2 rounded whitespace-pre-wrap font-mono">
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}

          <div className="flex gap-2">
            <button
              onClick={this.reset}
              className="px-3 py-1 bg-orange text-white text-sm rounded hover:opacity-90"
            >
              Try to recover
            </button>
            <button
              onClick={this.reload}
              className="px-3 py-1 border border-line-dark text-text text-sm rounded hover:bg-bg"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
